import React, { useState } from 'react';
import { BillOfLading } from 'src/components/BillOfLading';
import LineItemsCard from 'src/components/LineItemsCard';
import LoadingIndicator from 'src/components/LoadingIndicator';
import MapHeader from 'src/components/MapHeader';
import StatusProgress from 'src/components/StatusProgress';
import emptyImg from 'src/images/empty.svg';
import logo from 'src/images/logo-md.png';
import { usePreviewTrack } from 'src/lib/hooks';
import {
  AddressCard,
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Icon,
  Modal,
  ModalBody,
  Row,
  useApp,
  useScreenSize
} from 'straightline-ui';
import { roundNumberToLabel } from 'straightline-utils/accounting';
import {
  isCancelled,
  isDelivered,
  isFTL,
  STATUS,
  statusType
} from 'straightline-utils/constants';
import {
  differenceInSeconds,
  formatDistanceToNow
} from 'straightline-utils/time';

export default function ShipmentPreview({ match, location }) {
  const app = useApp();
  const { xl } = useScreenSize();
  const token = location.query.token;

  const {
    result: shipment,
    isLoading,
    isError,
    error
  } = app.service('api/shipment-previews').useGet(
    token,
    {},
    {
      enabled: !!token,
      refetchInterval: (result) => {
        if (!result) {
          return false;
        }
        // When newly created, there are still dispatches and other side
        // effects happening in the background. There may also be an SL
        // admin use making quick updates as this is created. Ping
        // for updates on new shipments
        const created = differenceInSeconds(new Date(), result.created_at);
        if (created <= 15) {
          return 3000;
        }
        if (created <= 60) {
          return 8000;
        }
        if (created <= 60 * 5) {
          return 15000;
        }
        return 60000;
      }
    }
  );

  usePreviewTrack(shipment, 'SHIPPER_PREVIEW');

  if (isLoading) {
    return (
      <div className="d-flex vh-100 align-items-center">
        <LoadingIndicator delay={250} />
      </div>
    );
  }

  if (isError || !token) {
    return (
      <Container>
        <Row className="align-items-center justify-content-center vh-100">
          <Col lg="6" className="text-center">
            <img
              src={emptyImg}
              alt="Not Found"
              className="img-fluid animated fadeIn faster mb-5"
            />
            {error?.code === 404 ? (
              <h1>Could not find that shipment</h1>
            ) : (
              <h1>{error?.message || 'Could not find that shipment'}</h1>
            )}
            <ContactModal shipment={shipment} />
          </Col>
        </Row>
      </Container>
    );
  }

  return (
    <div>
      <Container fluid className="fixed-top bg-white shadow-sm bg-white">
        <Row className="align-items-center py-2">
          <Col xs="4">
            <img src={logo} style={{ maxWidth: 138 }} alt="Straightline Logo" />
          </Col>
          <Col xs="8" className="ml-auto text-right">
            <a
              href="https://www.bookstraightline.com/shippers"
              target="_blank"
              rel="noreferrer"
              className="btn btn-white"
            >
              <Icon type="help-circle" className="mr-2" />
              Learn More
            </a>
          </Col>
        </Row>
      </Container>
      <div style={{ paddingTop: 54 }}>
        <div className="">
          <MapHeader shipment={shipment} />
          <StatusProgress
            shipment={shipment}
            unshift={window.innerWidth < 1050}
            trimFlat={true}
            unshiftFlat={true}
          />
        </div>
        <Container fluid="lg" className="mb-5">
          <PageHeader shipment={shipment} />
        </Container>
        <Container fluid="lg" className="pb-5">
          <Row>
            {xl ? (
              <React.Fragment>
                <Col xl="7">
                  <StatusCard shipment={shipment} />
                  <DetailsCard shipment={shipment} />
                  <LineItemsCard shipment={shipment} />
                  <BOLCard shipment={shipment} />
                </Col>
                <Col xl="5">
                  <AddressCard shipment={shipment} type="pickup" />
                  <AddressCard shipment={shipment} type="delivery" />
                </Col>
              </React.Fragment>
            ) : (
              <React.Fragment>
                <Col sm="12">
                  <StatusCard shipment={shipment} />
                  <DetailsCard shipment={shipment} />
                  <AddressCard shipment={shipment} type="pickup" />
                  <AddressCard shipment={shipment} type="delivery" />
                  <LineItemsCard shipment={shipment} />
                  <BOLCard shipment={shipment} />
                </Col>
              </React.Fragment>
            )}
          </Row>
        </Container>
      </div>
    </div>
  );
}

const PageHeader = ({ shipment }) => {
  return (
    <div className="header mb-0 border-bottom">
      <div className="header-body border-0">
        <Row className="font-weight-bold">
          <Col xs="auto" className="mb-3">
            <span className="text-muted">SL#: </span>
            {shipment.id}
          </Col>
          {shipment.reference_number && (
            <Col xs="auto" className="mb-3">
              <span className="text-muted">REF#: </span>
              {shipment.reference_number}
            </Col>
          )}
          {shipment.shipment_dispatch?.carrier_pro_number && (
            <Col xs="auto" className="mb-3">
              <span className="text-muted">PRO#: </span>
              {shipment.shipment_dispatch.carrier_pro_number}
            </Col>
          )}
          {shipment.mode && (
            <Col xs="auto" className="mb-3">
              <span className="text-muted">MODE: </span>
              {shipment.mode}
            </Col>
          )}
          {shipment.carrier && (
            <Col xs="auto" className="mb-3">
              <span className="text-muted">CARRIER: </span>
              {shipment.carrier.name}
            </Col>
          )}
          {shipment.rate && (
            <Col xs="auto" className="mb-3">
              <span className="text-muted">SERVICE: </span>
              {shipment.rate.service_level.name}
            </Col>
          )}
        </Row>
        <Row className="align-items-end justify-content-between mt-md-n1 mb-n3">
          <Col sm="auto" className="mb-3">
            <Row className="align-items-center mb-n3">
              <Col xs="auto" className="mb-3 pr-0 align-items-center d-flex">
                <h1 className="mb-0 font-weight-bold d-inline-block mr-3">{`${shipment.pickup_address.city}, ${shipment.pickup_address.state}`}</h1>
                <Icon
                  type="arrow-right"
                  style={{ fontSize: 24 }}
                  className="text-success lead d-inline-block font-weight-bold"
                />
              </Col>
              <Col xs="auto" className="mb-3">
                <h1 className="mb-0 font-weight-bold d-inline-block">{`${shipment.delivery_address.city}, ${shipment.delivery_address.state}`}</h1>
              </Col>
            </Row>
          </Col>
          <Col sm="auto" className="text-right mb-3">
            <Row className="d-flex mb-n3">
              <Col sm="auto" className="mb-3">
                {shipment.carrier_id && (
                  <Button
                    color="primary"
                    tag="a"
                    block
                    disabled={!shipment.bol_url}
                    href={`${shipment.bol_url}?download=true`}
                  >
                    <Icon type="download" className="mr-2" />
                    Download BOL
                  </Button>
                )}
              </Col>
              <Col sm="auto" className="mb-3">
                <ContactModal shipment={shipment} />
              </Col>
            </Row>
          </Col>
        </Row>
      </div>
    </div>
  );
};

const ContactModal = ({ shipment }) => {
  const [open, setOpen] = useState(false);
  const toggle = () => setOpen((open) => !open);

  return (
    <React.Fragment>
      <Modal
        centered
        isOpen={open}
        toggle={toggle}
        className="mx-auto"
        style={{ maxWidth: 425, width: '95%' }}
      >
        {() => (
          <ModalBody>
            <h2>We're here for you.</h2>
            <p>
              Straightline has a team of logistics professionals ready to help.
              Give us a shout and we will get back to you ASAP.
            </p>
            <p>
              <Icon type="phone" className="mr-3" />
              <a href={`tel:${process.env.REACT_APP_SUPPORT_PHONE}`}>
                {process.env.REACT_APP_SUPPORT_PHONE}
              </a>
            </p>
            <p>
              <Icon type="mail" className="mr-3" />
              <a
                href={`mailto:${
                  process.env.REACT_APP_SUPPORT_EMAIL
                }?subject=Shipment #${shipment?.id || 'NA'}`}
              >
                {process.env.REACT_APP_SUPPORT_EMAIL}
              </a>
            </p>
            <div className="mt-5">
              <Button
                block
                color="white"
                className="mr-3"
                type="button"
                onClick={toggle}
              >
                Close
              </Button>
            </div>
          </ModalBody>
        )}
      </Modal>
      <Button color="white" onClick={toggle} block>
        <Icon type="send" className="mr-2" />
        Contact Straightline
      </Button>
    </React.Fragment>
  );
};

const DetailsCard = ({ shipment }) => {
  return (
    <Card>
      <CardBody>
        <div>
          <h5 className="text-uppercase text-muted font-weight-bold">Truck</h5>
          <div className="pre-line mb-4">
            {shipment.truck.label}
            <Icon type="dot" className="text-muted mx-1" />
            {roundNumberToLabel(shipment.load_weight)} lbs
          </div>
        </div>
        <div>
          <div className="mb-4">
            <h5 className="text-uppercase text-muted font-weight-bold">
              Service
            </h5>
            <div>{shipment.rate.service_level.name}</div>
            <div>{shipment.rate.service_level.description}</div>
            {shipment.rate.service_level.code === 'TEM' && (
              <div className="font-weight-bold text-primary">
                ** This shipment requires two drivers **
              </div>
            )}
          </div>
        </div>
        <div>
          <h5 className="text-uppercase text-muted font-weight-bold">Notes</h5>
          <div className="pre-line">
            {shipment.notes || (
              <span className="text-muted">Not available</span>
            )}
          </div>
        </div>
      </CardBody>
    </Card>
  );
};

const StatusCard = ({ shipment }) => {
  if (isCancelled(shipment)) {
    return (
      <Card>
        <CardBody className="px-3 px-md-4">
          <Row className="justify-content-between align-items-center mb-3">
            <Col xs="auto">
              <div className="h3 mb-0 d-flex align-items-center">
                <Icon type="activity" className="mr-3" />
                Load Status
              </div>
            </Col>
            <Col xs="auto">
              <div className="progress progress-status">
                <div
                  className="bg-danger progress-bar"
                  style={{ width: '100%' }}
                >
                  Cancelled
                </div>
              </div>
            </Col>
          </Row>
          <div>
            This shipment has been cancelled. Don't worry! you won't be charged
            for this shipment. Repeat the shipment to get another quote. If you
            have any questions, please{' '}
            <ContactModal
              config={{
                subject: `App Contact - Shipment #${shipment.id}`,
                service: 'api/shipments',
                service_id: shipment.id
              }}
            >
              {(toggle) => (
                <span className="text-primary pointer" onClick={toggle}>
                  Contact Straightline
                </span>
              )}
            </ContactModal>
            .
          </div>
        </CardBody>
      </Card>
    );
  }

  if (isDelivered(shipment)) {
    return (
      <Card>
        <CardBody className="px-3 px-md-4">
          <Row className="justify-content-between align-items-center mb-3">
            <Col xs="auto">
              <div className="h3 mb-0 d-flex align-items-center">
                <Icon type="activity" className="mr-3" />
                Load Status
              </div>
            </Col>
            <Col xs="auto">
              <StatusProgress shipment={shipment} unshift trim />
            </Col>
          </Row>
          <div>
            This shipment has been successfully delivered! If you have any
            questions, please{' '}
            <ContactModal
              config={{
                subject: `App Contact - Shipment #${shipment.id}`,
                service: 'api/shipments',
                service_id: shipment.id
              }}
            >
              {(toggle) => (
                <span className="text-primary pointer" onClick={toggle}>
                  Contact Straightline
                </span>
              )}
            </ContactModal>
            .
          </div>
        </CardBody>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <StatusItem shipment={shipment} />
        <DriverItem shipment={shipment} />
        <EtaItem shipment={shipment} />
        <TrackingHistory shipment={shipment} />
      </Card>
    </>
  );
};

const TrackingHistory = ({ shipment }) => {
  const [showMore, setShowMore] = useState(false);
  const tracking = shipment.tracking || [];
  const latestTrack = tracking[0];

  if (!latestTrack) {
    return (
      <CardBody className="px-3 px-md-4">
        <Row className="align-items-center justify-content-between mb-3">
          <Col xs="auto">
            <div className="h3 mb-0 d-flex align-items-center">
              <Icon type="map" className="mr-3" />
              Tracking
              <Badge color="soft-secondary" className="ml-3">
                Waiting
              </Badge>
            </div>
          </Col>
          <Col xs="auto"></Col>
        </Row>
        <div>
          The carrier has not yet provided tracking information for this
          shipment. Tracking information may be available when the carrier
          dispatches the load.
        </div>
      </CardBody>
    );
  }

  const latestEvents = tracking.slice(0, 3);
  const restEvents = tracking.slice(3);

  return (
    <CardBody className="px-3 px-md-4">
      <Row className="align-items-center justify-content-between mb-3">
        <Col xs="auto">
          <div className="h3 mb-0 d-flex align-items-center">
            <Icon type="compass" className="mr-3" />
            Tracking
            {!isDelivered(shipment) && (
              <Badge color="soft-primary" className="ml-3">
                <Icon type="check" className="mr-1" />
                Active
              </Badge>
            )}
          </div>
        </Col>
        <Col xs="auto"></Col>
      </Row>
      <div className="py-3 px-4 border-left border-primary bg-lighter mb-3">
        {latestEvents.map((track, index) => {
          return (
            <div
              key={track.id}
              className={`d-flex ${index !== 0 ? 'mt-3' : ''}`}
            >
              <div>
                <Icon type="map-pin" className="text-secondary mr-3" />
              </div>
              <div className="flex-grow-1">
                <div className="font-weight-bold text-primary">
                  {formatAddress(track) || 'Unknown Location'}
                </div>
                <div className="text-secondary">
                  {new Date(track.carrier_timestamp).toLocaleDateString(
                    'en-US',
                    {
                      timeZoneName: 'short',
                      hour: 'numeric',
                      minute: 'numeric',
                      month: 'short',
                      weekday: 'short',
                      day: 'numeric'
                    }
                  )}
                </div>
                <div>
                  {track.description || (
                    <span className="text-secondary">Notes Not Available</span>
                  )}
                </div>
              </div>
            </div>
          );
        })}
        {restEvents.length > 0 && (
          <Button
            size="sm"
            color="white"
            className="mt-3"
            onClick={() => setShowMore(!showMore)}
          >
            {showMore ? 'Show Less' : `Show ${restEvents.length} More`}
          </Button>
        )}
        {showMore &&
          restEvents.map((track, index) => {
            return (
              <div key={track.id} className="d-flex mt-3">
                <div>
                  <Icon type="map-pin" className="text-secondary mr-3" />
                </div>
                <div className="flex-grow-1">
                  <div className="font-weight-bold text-primary">
                    {formatAddress(track) || 'Unknown Location'}
                  </div>
                  <div className="text-secondary">
                    {new Date(track.carrier_timestamp).toLocaleDateString(
                      'en-US',
                      {
                        timeZoneName: 'short',
                        hour: 'numeric',
                        minute: 'numeric',
                        month: 'short',
                        weekday: 'short',
                        day: 'numeric'
                      }
                    )}
                  </div>
                  <div>
                    {track.description || (
                      <span className="text-secondary">
                        Notes Not Available
                      </span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
      </div>
      {!isDelivered(shipment) && (
        <div className="">
          Carriers may provide additional tracking information as the shipment
          is in transit. View the map at the top of the page for the most recent
          location and route information
        </div>
      )}
    </CardBody>
  );
};

function EtaItem({ shipment }) {
  const etaType = shipment.status.order <= 3 ? 'pickup' : 'delivery';
  const etaAddress = shipment[`${etaType}_address`];
  const etaLabel = etaType === 'pickup' ? 'Pickup' : 'Delivery';
  const { eta } = shipment;
  const isReady = eta?.type === etaType;

  return (
    <CardBody className="border-bottom px-3 px-md-4">
      <Row className="align-items-center justify-content-between mb-3">
        <Col xs="auto" className="">
          <div className="h3 mb-0 d-flex align-items-center">
            <Icon type="clock" className="mr-3" />
            <span className="d-none d-sm-inline">Estimated {etaLabel}</span>
            <span className="d-sm-none">{etaLabel} ETA</span>
            {isReady ? (
              <Badge color="soft-primary" className="ml-3">
                <Icon type="check" className="mr-1" />
                ETA Set
              </Badge>
            ) : (
              <Badge color="soft-secondary" className="ml-3">
                Waiting
              </Badge>
            )}
          </div>
        </Col>
        <Col xs="auto" className="text-right"></Col>
      </Row>
      {isReady ? (
        <div>
          <div className="py-3 px-4 border-left border-primary bg-lighter mb-3">
            <div className="mb-2 d-flex align-items-start">
              <Icon type="calendar" className="text-secondary mr-3" />
              <div>
                <div className="text-primary font-weight-bold">
                  {new Date(eta.date).toLocaleDateString('en-US', {
                    timeZone: etaAddress.timezone,
                    timeZoneName: 'short',
                    hour: 'numeric',
                    minute: 'numeric',
                    month: 'short',
                    weekday: 'short',
                    day: 'numeric'
                  })}
                </div>
                <div className="text-secondary">
                  last updated {formatDistanceToNow(eta.created_at)}
                </div>
              </div>
            </div>
            <div className="d-flex align-items-start">
              <Icon type="file-text" className="text-secondary mr-3" />
              {eta.notes || (
                <span className="text-secondary">
                  No notes provided by carrier.
                </span>
              )}
            </div>
          </div>
          <div>
            The carrier may update the ETA as the shipment progresses.
            Notifications will be sent to you and the {eta.type} contact as
            ETA's are updated.
          </div>
        </div>
      ) : (
        <div>
          The shipment's estimated arrival time has not been determined yet.
          Straightline is coordinating with the carrier to establish an ETA.
        </div>
      )}
    </CardBody>
  );
}

function DriverItem({ shipment }) {
  if (!isFTL(shipment)) {
    return null;
  }

  const shipment_dispatch = shipment.shipment_dispatch;

  return (
    <CardBody className="border-bottom px-3 px-md-4">
      <Row className="align-items-center justify-content-between mb-3">
        <Col xs="auto">
          <div className="h3 mb-0 d-flex align-items-center">
            <Icon type="user" className="mr-3" />
            Driver
            {!!shipment_dispatch ? (
              <Badge color="soft-primary" className="ml-3">
                <Icon type="check" className="mr-1" />
                Dispatched
              </Badge>
            ) : (
              <Badge color="soft-secondary" className="ml-3">
                Waiting
              </Badge>
            )}
          </div>
        </Col>
        <Col xs="auto"></Col>
      </Row>
      {shipment_dispatch && (
        <div className="py-3 px-4 border-left border-primary bg-lighter mb-3">
          <div className="mb-1">
            <Icon type="user" className="text-secondary mr-3" />
            {shipment_dispatch.carrier_driver}
          </div>
          <div className="mb-1">
            <Icon type="phone" className="text-secondary mr-3" />
            {shipment_dispatch.carrier_phone ? (
              <a href={`tel:${shipment_dispatch.carrier_phone}`}>
                {shipment_dispatch.carrier_phone}
              </a>
            ) : (
              <span className="text-secondary">Not Available</span>
            )}
          </div>
          <div className="mb-1">
            <Icon type="truck" className="text-secondary mr-3" />
            {shipment_dispatch?.carrier_truck_number || (
              <span className="text-secondary">Not Available</span>
            )}
          </div>
          <div className="">
            <Icon type="book" className="text-secondary mr-3" />
            {shipment_dispatch?.carrier_trailer_number || (
              <span className="text-secondary">Not Available</span>
            )}
          </div>
        </div>
      )}
      {shipment_dispatch ? (
        <div>
          Verify the driver's contact information, truck number, and trailer
          number upon arrival. This information is also available on the BOL.
        </div>
      ) : (
        <div>
          This shipment has not yet been dispatched to a driver. Once
          dispatched, the driver's contact information, truck number, and
          trailer number will be available here.
        </div>
      )}
    </CardBody>
  );
}

const statusTexts = {
  [STATUS.BOOKED]: `The shipment has been quoted and officially booked with Straightline. We are now working on finding a carrier for you. Once a carrier is assigned, the shipment will then be moved to the "Matched" status.`,
  [STATUS.MATCHED]: `A carrier has been assigned to this shipment and is beginning preparations for pickup. The carrier will soon assign a driver and mark this shipment as "Dispatched". You can then download the BOL and prepare for pickup.`,
  [STATUS.DISPATCHED]: `A driver has been assigned to this shipment and is on their way to the pickup location. You can expect an ETA from the driver soon. Please prepare for pickup by ensuring the cargo is ready to load, the BOL is printed, and that the driver has access to the pickup location.`,
  [STATUS.PICKUP]: `The driver has arrived at the pickup location and is preparing to load the shipment. Please provide the BOL to the driver, ensure they have access to the pickup location, and assist them with any questions or concerns they may have.`,
  [STATUS.TRANSIT]: `The shipment is currently in transit and on its way to the delivery location. You will receive an ETA for delivery soon. Please ensure the delivery location is prepared for unload and that the driver has access to the location.`,
  [STATUS.DELIVERY]: `The driver has arrived at the delivery location and is preparing to unload the shipment. Please ensure the driver has access to the delivery location and assist them with any questions or concerns they may have.`,
  [STATUS.DELIVERED]: `The shipment has been successfully dropped off. Soon the driver will upload the POD and finalize the shipment. You will then be able to download the POD and view the billing details.`,
  [STATUS.CANCELLED]: `The shipment has been cancelled. Please contact Straightline if you have any questions or concerns.`
};

function StatusItem({ shipment }) {
  const currentStatus = statusType(shipment.status_type);

  return (
    <CardBody className="border-bottom px-3 px-md-4">
      <Row className="justify-content-between align-items-center mb-3">
        <Col xs="auto">
          <div className="h3 mb-0 d-flex align-items-center">
            <Icon type="activity" className="mr-3" />
            Load Status
          </div>
        </Col>
        <Col xs="auto">
          <StatusProgress shipment={shipment} unshift trim />
        </Col>
      </Row>
      <div>{statusTexts[currentStatus.value]}</div>
    </CardBody>
  );
}

const formatAddress = ({ city, state, postal_code, country }) => {
  const strings = [state, postal_code, country].filter(Boolean);
  if (!city) {
    return strings.join(' ');
  }
  if (!strings.length) {
    return city;
  }
  return `${city}, ${strings.join(' ')}`;
};

function BOLCard({ shipment }) {
  return (
    <div>
      {shipment.carrier_id ? (
        <div>
          <div className="p-3 bg-white mb-n2 d-inline-block border rounded-top">
            <Button
              color="primary"
              tag="a"
              disabled={!shipment.bol_url}
              href={`${shipment.bol_url}?download=true`}
            >
              <Icon type="download" className="mr-2" />
              Download BOL
            </Button>
          </div>
          <Card>
            <BillOfLading shipment={shipment} />
          </Card>
        </div>
      ) : (
        <div className="">
          <div className="rounded shadow position-relative">
            <div>
              <BillOfLading shipment={shipment} />
            </div>
            <div
              className="position-absolute text-secondary rounded pt-6 p-4"
              style={{
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgb(237 242 249 / 85%)'
              }}
            >
              <div
                className="text-secondary text-center"
                style={{ fontSize: 100 }}
              >
                DRAFT
              </div>
              <div className="alert alert-info">
                <p>
                  This shipment's BOL is not quite ready. Straightline may still
                  be negotiating with carriers or verifying information like PRO
                  numbers and accessorials. Check back soon or click the button
                  below to contact us.
                </p>
                <ContactModal shipment={shipment} />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
